import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type AcaoAuditoria, type EventoAuditoria } from '../dados/api.js'
import { banco } from '../dados/banco.js'
import {
  PainelAdmin,
  QuandoFoi,
  SemRedeAqui,
  corAcao,
  corAcaoAdmin,
  rotuloAcao,
  rotuloAcaoAdmin,
} from './admin-comum.js'

/// Trilha de auditoria. Só admin.
///
/// Duas perguntas diferentes, duas abas:
///
/// - **Alterações** — o que aconteceu com as inspeções: quem criou, editou, mudou
///   status, revisou criticidade, excluiu. É a trilha que importa quando uma
///   interdição é questionada.
/// - **Acessos** — quem entrou no sistema, quando e de qual aparelho.
///
/// A trilha começa vazia para o histórico migrado: a auditoria registra o que
/// acontece **neste** sistema, e o Power Apps não tinha trilha para trazer.

const ACOES: AcaoAuditoria[] = [
  'CRIACAO',
  'EDICAO',
  'MUDANCA_STATUS',
  'REVISAO_CRITICIDADE',
  'EXCLUSAO',
]

export function Auditoria() {
  const [aba, setAba] = useState<'alteracoes' | 'acessos' | 'administracao'>('alteracoes')

  return (
    <PainelAdmin titulo="Auditoria" subtitulo="O que foi feito no sistema, e por quem">
      <div className="mb-4 flex gap-1 rounded-lg bg-slate-100 p-1">
        <Aba ativa={aba === 'alteracoes'} onClick={() => setAba('alteracoes')}>
          Alterações
        </Aba>
        <Aba ativa={aba === 'acessos'} onClick={() => setAba('acessos')}>
          Acessos
        </Aba>
        <Aba ativa={aba === 'administracao'} onClick={() => setAba('administracao')}>
          Administração
        </Aba>
      </div>

      {aba === 'alteracoes' && <Alteracoes />}
      {aba === 'acessos' && <Acessos />}
      {aba === 'administracao' && <Administracao />}
    </PainelAdmin>
  )
}

function Aba({
  ativa,
  onClick,
  children,
}: {
  ativa: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      aria-current={ativa ? 'page' : undefined}
      className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
        ativa ? 'bg-white text-marinho shadow-sm' : 'text-slate-600 hover:text-slate-900'
      }`}
    >
      {children}
    </button>
  )
}

function Alteracoes() {
  const [acao, setAcao] = useState<AcaoAuditoria | ''>('')
  const [obraId, setObraId] = useState('')
  const [de, setDe] = useState('')

  // As obras já estão no aparelho pela sincronização; não precisa pedir de novo.
  const obras = useLiveQuery(() => banco.obras.orderBy('codigo').toArray(), [], [])

  const filtros = {
    acao: acao || undefined,
    obraId: obraId || undefined,
    de: de ? new Date(`${de}T00:00:00`).toISOString() : undefined,
  }

  const consulta = useInfiniteQuery({
    queryKey: ['admin', 'auditoria', filtros],
    queryFn: ({ pageParam }) =>
      api.adminAuditoria({ ...filtros, antesDe: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  })

  const eventos = consulta.data?.pages.flatMap((p) => p.eventos) ?? []

  return (
    <>
      <div className="cartao mb-4 flex flex-wrap gap-2 p-3">
        <select value={acao} onChange={(e) => setAcao(e.target.value as AcaoAuditoria | '')} className="campo">
          <option value="">Todas as ações</option>
          {ACOES.map((a) => (
            <option key={a} value={a}>
              {rotuloAcao[a]}
            </option>
          ))}
        </select>
        <select value={obraId} onChange={(e) => setObraId(e.target.value)} className="campo">
          <option value="">Todas as obras</option>
          {obras.map((o) => (
            <option key={o.id} value={o.id}>
              {o.codigo} — {o.nome}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          A partir de
          <input type="date" value={de} onChange={(e) => setDe(e.target.value)} className="campo" />
        </label>
      </div>

      {consulta.isError && <SemRedeAqui erro={consulta.error} />}
      {consulta.isLoading && <p className="py-8 text-center text-slate-500">Carregando…</p>}

      {consulta.data && eventos.length === 0 && (
        <div className="cartao p-6 text-center">
          <p className="font-semibold text-slate-700">Nenhum registro ainda</p>
          <p className="mt-1 text-sm text-slate-500">
            A trilha registra o que acontece neste sistema. O histórico migrado do Power Apps não
            trouxe auditoria porque lá não havia.
          </p>
        </div>
      )}

      <ol className="grid gap-2 lg:grid-cols-2 lg:items-start">
        {eventos.map((e) => (
          <li key={e.id}>
            <Evento evento={e} />
          </li>
        ))}
      </ol>

      {consulta.hasNextPage && (
        <button
          onClick={() => void consulta.fetchNextPage()}
          disabled={consulta.isFetchingNextPage}
          className="botao-neutro mt-4 w-full"
        >
          {consulta.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
        </button>
      )}
    </>
  )
}

function Evento({ evento }: { evento: EventoAuditoria }) {
  const alteracoes = Object.entries(evento.alteracoes ?? {})
  return (
    <article className="cartao p-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className={`etiqueta ${corAcao[evento.acao]}`}>{rotuloAcao[evento.acao]}</span>
        <Link to={`/inspecao/${evento.inspecao.id}/ver`} className="font-mono text-sm text-marinho underline">
          {evento.inspecao.codigo}
        </Link>
        <span className="text-xs text-slate-500">
          {evento.inspecao.obra.codigo} · {evento.inspecao.obra.nome}
        </span>
        <span className="ml-auto text-xs text-slate-500">
          <QuandoFoi iso={evento.em} />
        </span>
      </div>

      <p className="mt-1 text-sm text-slate-700">
        {evento.usuario.nome}{' '}
        <span className="text-xs text-slate-500">{evento.usuario.email}</span>
      </p>

      {alteracoes.length > 0 && (
        <dl className="mt-2 space-y-0.5 border-t border-slate-100 pt-2 text-xs">
          {alteracoes.map(([campo, mudanca]) => (
            <div key={campo} className="flex flex-wrap gap-x-2">
              <dt className="font-medium text-slate-600">{campo}</dt>
              <dd className="text-slate-500">
                <span className="line-through">{formatar(mudanca?.de)}</span>
                {' → '}
                <span className="font-medium text-slate-800">{formatar(mudanca?.para)}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </article>
  )
}

function formatar(valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '—'
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não'
  if (Array.isArray(valor)) return valor.length ? valor.join(', ') : '—'
  if (typeof valor === 'object') return JSON.stringify(valor)
  return String(valor)
}

function Acessos() {
  const [pagina, setPagina] = useState(1)
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'acessos', pagina],
    queryFn: () => api.adminAcessos({ pagina }),
    placeholderData: (anterior) => anterior,
  })

  if (isError) return <SemRedeAqui erro={error} />
  if (isLoading) return <p className="py-8 text-center text-slate-500">Carregando…</p>
  if (!data) return null

  if (data.acessos.length === 0) {
    return (
      <div className="cartao p-6 text-center">
        <p className="font-semibold text-slate-700">Nenhum acesso registrado</p>
        <p className="mt-1 text-sm text-slate-500">Ninguém entrou no sistema ainda.</p>
      </div>
    )
  }

  return (
    <>
      <p className="mb-2 text-sm text-slate-600">
        {data.total} {data.total === 1 ? 'sessão registrada' : 'sessões registradas'}
      </p>
      <ul className="grid gap-2 lg:grid-cols-2 lg:items-start">
        {data.acessos.map((a) => {
          const vencido = new Date(a.expiraEm) <= new Date()
          return (
            <li key={a.id} className="cartao p-3">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold text-slate-800">{a.usuario.nome}</span>
                <span className="text-xs text-slate-500">{a.usuario.email}</span>
                <span className="ml-auto text-xs text-slate-500">
                  <QuandoFoi iso={a.criadoEm} />
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                <span>{a.descricao ?? 'Aparelho'}</span>
                {a.ultimoUso && (
                  <span>
                    · último uso <QuandoFoi iso={a.ultimoUso} />
                  </span>
                )}
                {a.revogadoEm ? (
                  <span className="etiqueta bg-slate-200 text-slate-600">revogado</span>
                ) : vencido ? (
                  <span className="etiqueta bg-slate-200 text-slate-600">expirado</span>
                ) : (
                  <span className="etiqueta bg-leve/10 text-leve">sessão ativa</span>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      {data.paginas > 1 && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            onClick={() => setPagina(pagina - 1)}
            disabled={pagina <= 1 || isFetching}
            className="botao-neutro"
          >
            ← Anterior
          </button>
          <span className="text-sm text-slate-600">
            {data.pagina} de {data.paginas}
          </span>
          <button
            onClick={() => setPagina(pagina + 1)}
            disabled={pagina >= data.paginas || isFetching}
            className="botao-neutro"
          >
            Próxima →
          </button>
        </div>
      )}
    </>
  )
}

/// Quem mexeu no acesso de quem. É a trilha que responde "por que fulano não
/// consegue mais entrar?" — e a que protege quem administra, mostrando que a ação
/// teve autor, data e motivo.
function Administracao() {
  const consulta = useInfiniteQuery({
    queryKey: ['admin', 'acoes'],
    queryFn: ({ pageParam }) => api.adminAcoes(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  })

  if (consulta.isError) return <SemRedeAqui erro={consulta.error} />
  if (consulta.isLoading) return <p className="py-8 text-center text-slate-500">Carregando…</p>

  const acoes = consulta.data?.pages.flatMap((p) => p.acoes) ?? []

  if (acoes.length === 0) {
    return (
      <div className="cartao p-6 text-center">
        <p className="font-semibold text-slate-700">Nenhuma ação registrada</p>
        <p className="mt-1 text-sm text-slate-500">
          Nada de acesso, desativação ou exclusão foi feito até agora.
        </p>
      </div>
    )
  }

  return (
    <>
      <ol className="grid gap-2 lg:grid-cols-2 lg:items-start">
        {acoes.map((a) => (
          <li key={a.id} className="cartao p-3">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className={`etiqueta ${corAcaoAdmin[a.acao]}`}>{rotuloAcaoAdmin[a.acao]}</span>
              <span className="font-semibold text-slate-800">{a.alvoNome}</span>
              <span className="text-xs text-slate-500">{a.alvoEmail}</span>
              <span className="ml-auto text-xs text-slate-500">
                <QuandoFoi iso={a.em} />
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {a.detalhes?.origem === 'grupo' ? 'pelo grupo do Entra ID, no login' : `por ${a.autor.nome}`}
              {a.detalhes?.perfis && ` · ${a.detalhes.perfis.join(', ')}`}
              {a.detalhes?.obras && a.detalhes.obras.length > 0 && ` · obras ${a.detalhes.obras.join(', ')}`}
              {a.detalhes?.incluidas && a.detalhes.incluidas.length > 0 && ` · incluiu ${a.detalhes.incluidas.join(', ')}`}
              {a.detalhes?.removidas && a.detalhes.removidas.length > 0 && ` · retirou ${a.detalhes.removidas.join(', ')}`}
              {a.detalhes?.tentativaDeExclusao && ' · exclusão pedida, desativado por ter histórico'}
            </p>
            {a.detalhes?.motivo && (
              <p className="mt-1 border-t border-slate-100 pt-1 text-xs italic text-slate-600">
                "{a.detalhes.motivo}"
              </p>
            )}
          </li>
        ))}
      </ol>

      {consulta.hasNextPage && (
        <button
          onClick={() => void consulta.fetchNextPage()}
          disabled={consulta.isFetchingNextPage}
          className="botao-neutro mt-4 w-full"
        >
          {consulta.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
        </button>
      )}
    </>
  )
}
